<template>
	<div>
		<!-- Filters -->
		<user-management-list-filters
			:options="options"
			:roles-filter.sync="rolesFilter"
			:departments-filter.sync="departmentsFilter"
			:status-filter.sync="statusFilter"
		/>

		<!-- Table Container Card -->
		<b-card
			no-body
			class="mb-0"
		>
			<div class="m-2">
				<!-- Table Top -->
				<b-row>
					<!-- Per Page -->
					<b-col
						cols="12"
						md="4"
						class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
					>
						<label>Mostrar</label>
						<v-select
							v-model="perPage"
							:dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
							:options="perPageOptions"
							:clearable="false"
							class="per-page-selector d-inline-block mx-50"
						/>
						<label>registros</label>
					</b-col>

					<!-- Search -->
					<b-col
						cols="12"
						md="6"
						class="ml-auto"
					>
						<div class="d-flex">
							<b-form-input
								v-model="searchQuery"
								class="d-inline-block mr-1"
								placeholder="Buscar ..."
								trim
							/>
							<b-button
								:to="{ name: 'user-management-create' }"
								variant="primary"
							>
								<span class="text-nowrap">Nuevo Usuario</span>
							</b-button>
						</div>
					</b-col>
				</b-row>
			</div>

			<b-table
				ref="refUserListTable"
				class="position-relative"
				:style="totalUsers >= 1 && totalUsers <= 3 ? 'height: 280px' : 'height: inherit'"
				:items="fetchUsers"
				responsive
				:fields="tableColumns"
				primary-key="id"
				:sort-by.sync="sortBy"
				show-empty
				empty-text="No se encontraron registros coincidentes"
				:sort-desc.sync="isSortDirDesc"
			>
				<template #table-busy>
					<div class="text-center text-primary my-2">
						<b-spinner class="align-middle mb-1"></b-spinner>
						<div>
							<b>CARGANDO</b>
						</div>
					</div>
				</template>

				<template #cell(fullName)="data">
					<div style="width: 300px;">
						<b-media vertical-align="center">
							<template #aside>
								<b-avatar
									size="32"
									:src="null"
									:text="avatarText(data.item.fullName)"
									variant="light-primary"
									:to="{ name: 'user-management-edit', params: { id: data.item.id } }"
								/>
							</template>
							<b-link
								:to="{ name: 'user-management-edit', params: { id: data.item.id } }"
								class="font-weight-bold d-block"
							>{{ data.item.fullName }}</b-link>
						</b-media>
					</div>
				</template>

				<template #cell(email)="data">
					<div class="text-nowrap">{{ data.item.email }}</div>
				</template>

				<template #cell(role_name)="data">
					<div class="text-nowrap">
						<b-badge
							:variant="data.item.role_id === 2 ? 'light-info' : 'light-warning'"
						>{{ data.item.role_name }}</b-badge>
					</div>
				</template>

				<template #cell(department_name)="data">
					<div class="text-nowrap">{{ data.item.department_name }}</div>
				</template>

				<template #cell(position_name)="data">
					<div style="width: 200px; margin: 0 auto;">{{ data.item.position_name || '-' }}</div>
				</template>

				<template #cell(status)="data">
					<div class="text-nowrap">
						<b-badge
							:variant="data.item.status ? 'light-success' : 'light-danger'"
						>{{ data.item.status ? 'Activo' : 'Inactivo' }}</b-badge>
					</div>
				</template>

				<template #cell(actions)="data">
					<b-dropdown
						variant="link"
						no-caret
						:right="$store.state.appConfig.isRTL"
					>
						<template #button-content>
							<feather-icon
								icon="MoreVerticalIcon"
								size="16"
								class="align-middle text-body"
							/>
						</template>

						<b-dropdown-item :to="{ name: 'user-management-edit', params: { id: data.item.id } }">
							<feather-icon icon="EditIcon" />
							<span class="align-middle ml-50">Editar</span>
						</b-dropdown-item>

						<b-dropdown-item @click="sendCredentials(data.item.id)">
							<feather-icon icon="MailIcon" />
							<span class="align-middle ml-50">Reenviar Credenciales</span>
						</b-dropdown-item>

						<b-dropdown-item @click="changeStatus(data.item.id, data.item.status)">
							<feather-icon :icon="data.item.status ? 'XCircleIcon' : 'CheckCircleIcon'" />
							<span class="align-middle ml-50">{{ data.item.status ? 'Desactivar' : 'Activar' }}</span>
						</b-dropdown-item>
					</b-dropdown>
				</template>
			</b-table>

			<div class="mx-2 mb-2">
				<b-row>
					<b-col
						cols="12"
						sm="6"
						class="d-flex align-items-center justify-content-center justify-content-sm-start"
					>
						<span
							class="text-muted"
						>Mostrando {{ dataMeta.from }} al {{ dataMeta.to }} de {{ dataMeta.of }} registros</span>
					</b-col>

					<b-col
						cols="12"
						sm="6"
						class="d-flex align-items-center justify-content-center justify-content-sm-end"
					>
						<b-pagination
							v-model="currentPage"
							:total-rows="totalUsers"
							:per-page="perPage"
							first-number
							last-number
							class="mb-0 mt-1 mt-sm-0"
							prev-class="prev-item"
							next-class="next-item"
						>
							<template #prev-text>
								<feather-icon
									icon="ChevronLeftIcon"
									size="18"
								/>
							</template>
							<template #next-text>
								<feather-icon
									icon="ChevronRightIcon"
									size="18"
								/>
							</template>
						</b-pagination>
					</b-col>
				</b-row>
			</div>
		</b-card>
	</div>
</template>

<script>
import { ref, onBeforeMount, onUnmounted } from "@vue/composition-api"
import UserManagementListFilters from "./UserManagementListFilters.vue"
import userManagementStoreModule from "../userManagementStoreModule"
import useUserManagementList from "./useUserManagementList"
import { avatarText } from "@core/utils/filter"
import VueSweetalert2 from "vue-sweetalert2"
import useNotifications from "@notifications"
import vSelect from "vue-select"
import store from "@/store"
import axios from "@axios"
import Vue from "vue"
import "animate.css"

Vue.use(VueSweetalert2)

export default {
	components: {
		UserManagementListFilters,
		vSelect
	},
	setup() {
		// USE TOAST
		const { swalNotification, toastNotification } = useNotifications()

		// REFS
		const options = ref({})

		const USER_MGMT_STORE_NAME = "app-user-management"

		// REGISTER MODULE
		if (!store.hasModule(USER_MGMT_STORE_NAME))
			store.registerModule(USER_MGMT_STORE_NAME, userManagementStoreModule)

		// UNREGISTER ON LEAVE
		onUnmounted(() => {
			if (store.hasModule(USER_MGMT_STORE_NAME))
				store.unregisterModule(USER_MGMT_STORE_NAME)
		})

		// CALL FUNCTION TO POPULATE OPTIONS FOR THE FILTERS
		onBeforeMount(() => {
			getSelectFilters()
		})

		const getSelectFilters = async () => {
			try {
				const response1 = await axios.get("/selectors/departments")

				const roles = [
					{ value: 2, label: 'Administrador' },
					{ value: 3, label: 'Gestor' }
				]

				const statuses = [
					{ value: 1, label: 'Activo' },
					{ value: 0, label: 'Inactivo' }
				]

				options.value = {
					...response1.data,
					roles,
					statuses
				}
			} catch (error) {
				toastNotification(error.response.data.message, false)
			}
		}

		const sendCredentials = (id) => {
			store
				.dispatch("app-user-management/resendCredentials", { id })
				.then((response) => {
					swalNotification(response.data.message)
				})
				.catch((error) => {
					const message =
						error.response.data.message ||
						"Error al enviar las credenciales."
					swalNotification(message, false)
				})
		}

		const changeStatus = (id, currentStatus) => {
			if (currentStatus) {
				// DESACTIVAR
				Vue.swal({
					title: "¿Está seguro(a) de desactivar este usuario?",
					html: "<p>Se le <b>inhabilitarán los accesos</b> a la plataforma.</p><small>Los registros históricos del usuario se mantendrán.</small>",
					icon: "warning",
					showCancelButton: true,
					confirmButtonText: "Sí, continuar",
					cancelButtonText: "No, cancelar",
					customClass: {
						confirmButton: "btn btn-warning",
						cancelButton: "btn btn-outline-danger ml-1"
					},
					showClass: {
						popup: "animate__animated animate__tada"
					},
					buttonsStyling: false
				}).then((result) => {
					if (result.value) {
						dispatchToggleStatus(id, 'desactivar')
					}
				})
			} else {
				// ACTIVAR: confirmación indicando que se reenviarán credenciales
				Vue.swal({
					title: "¿Está seguro(a) de activar este usuario?",
					html: "<p>Se le <b>restablecerán los accesos</b> a la plataforma y se le <b>enviarán nuevamente las credenciales</b> al correo electrónico registrado.</p><small>Los registros históricos del usuario se han mantenido.</small>",
					icon: "info",
					showCancelButton: true,
					confirmButtonText: "Sí, activar!",
					cancelButtonText: "Cancelar",
					customClass: {
						confirmButton: "btn btn-primary",
						cancelButton: "btn btn-outline-danger ml-1"
					},
					showClass: {
						popup: "animate__animated animate__tada"
					},
					buttonsStyling: false
				}).then((result) => {
					if (result.value) {
						dispatchToggleStatus(id, 'activar')
					}
				})
			}
		}

		const dispatchToggleStatus = (id, actionText) => {
			store
				.dispatch("app-user-management/toggleStatus", { id })
				.then((response) => {
					refetchData()
					swalNotification(response.data.message)
				})
				.catch((error) => {
					const message =
						error.response.data.message || `Error al ${actionText} el usuario.`
					swalNotification(message, false)
				})
		}

		const {
			fetchUsers,
			tableColumns,
			perPage,
			currentPage,
			totalUsers,
			dataMeta,
			perPageOptions,
			searchQuery,
			sortBy,
			isSortDirDesc,
			refUserListTable,
			refetchData,

			// EXTRA FILTERS
			rolesFilter,
			departmentsFilter,
			statusFilter
		} = useUserManagementList()

		return {
			fetchUsers,
			tableColumns,
			options,
			perPage,
			currentPage,
			totalUsers,
			dataMeta,
			perPageOptions,
			searchQuery,
			sortBy,
			isSortDirDesc,
			refUserListTable,
			refetchData,

			// EXTRA FILTERS
			rolesFilter,
			departmentsFilter,
			statusFilter,

			// ACTIONS
			avatarText,
			sendCredentials,
			changeStatus
		}
	}
}
</script>

<style lang="scss" scoped>
.media {
	align-items: center;
}

.per-page-selector {
	width: 90px;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-sweetalert.scss";
</style>
